import { scan } from 'react-scan';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import * as TE from 'fp-ts/lib/TaskEither';
import * as E from 'fp-ts/lib/Either';
import { Some } from '@sniptt/monads';
import { getCookieTokens, updateTokens } from '@schemeserve/global-utils';
import { initializeServices } from './initialization/services';
import initReactApps from './initialization/initReactApps';
import {
  defineDevGlobals,
  createDevModuleContainers,
} from './initialization/devEnvironment';
import './assets/css/index.css';
import { reactModules } from './data/modules';
import {
  setWebpackPublicPath,
  initErrorMonitoring,
  insertAdminAreaOnlyStyles,
  appInsights,
} from './initialization/quickOps';
import {
  initAuth,
  INTERVAL,
  isAnAuthPage,
  isANonAuthPage,
  isInSchemeServe,
  logout,
  runProcess,
  setTokens,
} from './context/Auth';
import setUpFeautreFlags, {
  buildUserContext,
  getGrowthbookProvider,
} from './services/featureFlags';
import createFeatureDecisions from './services/feauterDecisions';
import { newAuthProcess, newLogout } from './context/NewAuth';

async function startUpdatedAuthProcess(refreshUrl, metricLogger) {
  console.debug('starting auth process', INTERVAL);
  metricLogger.trackEvent('auth_cookies');
  const process = pipe(
    newAuthProcess(refreshUrl, metricLogger),
    TE.tap(updateTokens),
    TE.tap(() =>
      TE.rightIO(() => {
        console.debug('token refresh successful');
        metricLogger.trackEvent({ name: 'refresh-success' });
      }),
    ),
    TE.tapError((error) =>
      TE.fromIO(() => {
        console.error(`Auth Process error: ${error}`);
        metricLogger.trackException({
          exception: new Error(error),
          properties: {
            reason: error,
          },
        });
      }),
    ),
    TE.match(
      (error) => newLogout(metricLogger, error),
      (tokens) => tokens,
    ),
  );

  await process();

  setInterval(async () => {
    await process();
  }, INTERVAL);
}

async function startAuthProcess(refreshUrl, metricLogger) {
  let interval;

  if (isInSchemeServe() && !isANonAuthPage()) {
    const userIsLoggedIn = initAuth(metricLogger);
    userIsLoggedIn?.match({
      ok: () => {
        interval = setInterval(async () => {
          const process = runProcess(refreshUrl, metricLogger);
          const res = await process();
          pipe(
            res,
            E.tap((tokens) => pipe(tokens, Some, setTokens, TE.right)),
            E.orElse((err) => {
              clearInterval(interval);
              metricLogger.trackException({
                exception: new Error(err),
                properties: {
                  reason: 'process exception',
                },
              });
              return logout(metricLogger, 'refresh-error');
            }),
          );
        }, INTERVAL);
      },
      err: async (err) => {
        metricLogger.trackException({
          exception: new Error(err),
          properties: {
            reason: 'initAuth failed',
          },
        });
        await logout(metricLogger, 'init-loggin-error');
      },
    });
  }
}

function getApiRoot() {
  return (
    window?.ENVIRONMENT?.ApiRoot?.Base ?? 'https://api.dev.schemeserve.com'
  );
}

function getSecureRoot() {
  return window?.ENVIRONMENT?.SecureRoot ?? 'https://acme.dev.schemeserve.com';
}

async function initFrontend() {

  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    scan({
      enabled: true,
      log: true, // logs render info to console (default: false)
    });
  }
  if (process.env.NODE_ENV === 'development') {
    createDevModuleContainers(reactModules);
    defineDevGlobals();
    initErrorMonitoring();
  } else {
    insertAdminAreaOnlyStyles();
    initErrorMonitoring();
  }
  /*
    * TODO remove once happy with new way of checking isLoggedIn
  window.isLoggedIn = !!(
    window.User &&
    window.User.AuthInfo &&
    window.User.AuthInfo.accessToken
  );
  */

  window.isLoggedIn = !!O.isSome(getCookieTokens());

  window.isFrontendEnabled = !!(
    window.Master &&
    window.Master.ReactModules &&
    window.Master.ReactModules.ActivateReact
  );

  if (process.env.NODE_ENV !== 'development') {
    setWebpackPublicPath();
  }

  const refreshUrl = window.location.host.includes('localhost')
    ? `${getApiRoot()}/auth/refresh`
    : `${getSecureRoot()}schemeserve/RefreshAccessToken.ashx`;

  const featureFlags = await setUpFeautreFlags(
    getGrowthbookProvider(),
    buildUserContext,
  );
  const featureDecisions = createFeatureDecisions(featureFlags);

  await initializeServices();

  if (featureDecisions.useCookieAuthProcess()) {
    console.debug({
      onAnAuthPage: !isAnAuthPage(),
    });
    if (!isAnAuthPage()) {
      startUpdatedAuthProcess(refreshUrl, appInsights);
    }
  } else {
    startAuthProcess(refreshUrl, appInsights);
  }

  initReactApps(reactModules, featureFlags);
}

initFrontend();
